import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiMapPin,
  FiPhone,
  FiMail,
  FiClock,
  FiSend,
  FiCheck,
  FiAlertCircle,
  FiLoader,
} from "react-icons/fi";
import { useInView } from "../components/useInView";
import "./Contact.css";

/* ── hero background — matches About page pattern ── */
import heroBg from "../assets/uploads/about/1771700784083-802325146.jpg";

/* ── disable right-click ── */
function useNoContextMenu() {
  useEffect(() => {
    const handler = (e) => e.preventDefault();
    document.addEventListener("contextmenu", handler);
    return () => document.removeEventListener("contextmenu", handler);
  }, []);
}

/* ════════════════════════════════════════════════════════════ */
export default function Contact() {
  useNoContextMenu();

  /* ── company contact data from API ── */
  const [contactInfo, setContactInfo] = useState(null);
  const [loadingContact, setLoadingContact] = useState(true);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/company-contact`)
      .then((r) => r.json())
      .then((data) => setContactInfo(data?.id ? data : null))
      .catch((err) => console.error("Company contact fetch failed:", err))
      .finally(() => setLoadingContact(false));
  }, []);

  /* ── Intersection observers ── */
  const [heroRef,  heroInView]  = useInView();
  const [cardsRef, cardsInView] = useInView();
  const [formRef,  formInView]  = useInView();
  const [mapRef,   mapInView]   = useInView();

  /* ── form state ── */
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", subject: "", message: "",
  });
  const [status, setStatus]     = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/send-email`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Server error ${res.status}`);

      setStatus("success");
      setForm({ name: "", email: "", phone: "", company: "", subject: "", message: "" });
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.message || "Something went wrong. Please try again.");
    }
  };

  /* ── Derived contact info with fallbacks ── */
  const phone         = contactInfo?.phone_number        || null;
  const email         = contactInfo?.email_address       || null;
  const address       = contactInfo?.physical_address    || null;
  const addressLink   = contactInfo?.physical_address_link || "#";
  /* address_iframe_link may be stored as a full <iframe> tag or a bare URL —
     extract just the src value either way */
  const rawIframe  = contactInfo?.address_iframe_link || null;
  const iframeLink = rawIframe
    ? (() => {
        const srcMatch = rawIframe.match(/src=["']([^"']+)["']/i);
        return srcMatch ? srcMatch[1] : rawIframe.trim();
      })()
    : null;

  /* Split multi-line address into lines for display */
  const addressLines = address
    ? address.split(/\n|,/).map((l) => l.trim()).filter(Boolean)
    : ["Address not available"];

  /* Split multi-number phone (newline or /) */
  const phoneLines = phone
    ? phone.split(/[\n/]/).map((p) => p.trim()).filter(Boolean)
    : [];

  /* Split multi-email */
  const emailLines = email
    ? email.split(/[\n,]/).map((e) => e.trim()).filter(Boolean)
    : [];

  /* ── Build contact cards dynamically from API data ── */
  const contactCards = [
    {
      icon:   FiMapPin,
      label:  "Visit Our Workshop",
      lines:  loadingContact ? ["Loading…"] : addressLines,
      action: address
        ? { text: "Get Directions", href: addressLink }
        : null,
    },
    {
      icon:   FiPhone,
      label:  "Call Us",
      lines:  loadingContact ? ["Loading…"] : phoneLines.length ? phoneLines : ["Not available"],
      action: phoneLines.length
        ? { text: "Call Now", href: `tel:${phoneLines[0].replace(/\s/g, "")}` }
        : null,
    },
    {
      icon:   FiMail,
      label:  "Email Us",
      lines:  loadingContact ? ["Loading…"] : emailLines.length ? emailLines : ["Not available"],
      action: emailLines.length
        ? { text: "Send Email", href: `mailto:${emailLines[0]}` }
        : null,
    },
    {
      icon:   FiClock,
      label:  "Workshop Hours",
      lines:  ["Mon – Fri: 07:30 – 17:00", "Saturday: 08:00 – 13:00", "Sunday: Closed"],
      action: null,
    },
  ];

  return (
    <main className="ct">

      {/* ══════ 1. HERO ══════ */}
      <section className="ct-hero" style={{ backgroundImage: `url(${heroBg})` }}>
        <div className="ct-hero__overlay" />
        <div className="container ct-hero__body" ref={heroRef}>
          <div className={`ct-hero__label fade-in ${heroInView ? "visible" : ""}`}>
            CONTACT US
          </div>
          <h1
            className={`ct-hero__title fade-in ${heroInView ? "visible" : ""}`}
            style={{ transitionDelay: "0.1s" }}
          >
            YOUR VEHICLE DESERVES<br />EXPERT CARE
          </h1>
          <p
            className={`ct-hero__subtitle fade-in ${heroInView ? "visible" : ""}`}
            style={{ transitionDelay: "0.2s" }}
          >
            Reach out to our specialist team for diagnostics, repairs and servicing — we're here to keep you on the road.
          </p>
        </div>
        <div className="ct-hero__crumb">
          <Link to="/">Home</Link>
          <FiArrowRight size={13} />
          <span>Contact Us</span>
        </div>
      </section>

      {/* ══════ 2. CONTACT CARDS ══════ */}
      <section className="ct-cards" ref={cardsRef}>
        <div className="container ct-cards__grid">
          {contactCards.map((card, i) => {
            const Icon = card.icon;
            return (
              <div
                key={i}
                className={`ct-card fade-in ${cardsInView ? "visible" : ""}`}
                style={{ transitionDelay: `${i * 0.1}s` }}
              >
                <div className="ct-card__icon-wrap">
                  <Icon size={24} />
                </div>
                <div className="ct-card__label">{card.label}</div>
                <div className="ct-card__lines">
                  {card.lines.map((l, j) => (
                    <span key={j}>{l}</span>
                  ))}
                </div>
                {card.action && (
                  <a
                    href={card.action.href}
                    className="ct-card__action"
                    target={card.action.href.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                  >
                    {card.action.text} <FiArrowRight size={13} />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ══════ 3. FORM + SIDEBAR ══════ */}
      <section className="ct-main" ref={formRef}>
        <div className="container ct-main__inner">

          {/* ── Left: contact form ── */}
          <div className={`ct-form-wrap fade-in ${formInView ? "visible" : ""}`}>
            <div className="ct-section-tag">SEND A MESSAGE</div>
            <h2 className="ct-section-h2">
              Get In Touch With<br /><span>Our Workshop</span>
            </h2>
            <p className="ct-section-sub">
              Fill in the form below and one of our technicians or service advisors will respond within one business day.
            </p>

            <form className="ct-form" onSubmit={handleSubmit} noValidate>
              <div className="ct-form__row">
                <div className="ct-form__field">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    id="name" name="name" type="text" required
                    placeholder="John Smith"
                    value={form.name} onChange={handleChange}
                    disabled={status === "sending"}
                  />
                </div>
                <div className="ct-form__field">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    id="email" name="email" type="email" required
                    placeholder="john@example.co.za"
                    value={form.email} onChange={handleChange}
                    disabled={status === "sending"}
                  />
                </div>
              </div>

              <div className="ct-form__row">
                <div className="ct-form__field">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    id="phone" name="phone" type="tel"
                    placeholder="012 000 0000"
                    value={form.phone} onChange={handleChange}
                    disabled={status === "sending"}
                  />
                </div>
                <div className="ct-form__field">
                  <label htmlFor="company">Vehicle Make &amp; Model</label>
                  <input
                    id="company" name="company" type="text"
                    placeholder="e.g. Toyota Fortuner 2020"
                    value={form.company} onChange={handleChange}
                    disabled={status === "sending"}
                  />
                </div>
              </div>

              <div className="ct-form__field">
                <label htmlFor="subject">Service Required *</label>
                <select
                  id="subject" name="subject" required
                  value={form.subject} onChange={handleChange}
                  disabled={status === "sending"}
                >
                  <option value="">— Select a service —</option>
                  <option value="Full Service">Full Vehicle Service</option>
                  <option value="Engine Diagnostics">Engine Diagnostics</option>
                  <option value="Brake Repairs">Brake Repairs</option>
                  <option value="Suspension & Steering">Suspension &amp; Steering</option>
                  <option value="Gearbox & Clutch">Gearbox &amp; Clutch</option>
                  <option value="Electrical Faults">Electrical Faults</option>
                  <option value="Air Conditioning">Air Conditioning</option>
                  <option value="Tyres & Alignment">Tyres &amp; Wheel Alignment</option>
                  <option value="Panel Beating">Panel Beating &amp; Body Repairs</option>
                  <option value="Quote Request">Request a Quote</option>
                  <option value="General">General Enquiry</option>
                </select>
              </div>

              <div className="ct-form__field">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message" name="message" required
                  rows={6}
                  placeholder="Describe the issue with your vehicle, any warning lights, sounds, or symptoms you've noticed…"
                  value={form.message} onChange={handleChange}
                  disabled={status === "sending"}
                />
              </div>

              {/* Status feedback */}
              {status === "success" && (
                <div className="ct-form__feedback ct-form__feedback--success">
                  <FiCheck size={18} />
                  <span>Thank you! Your message has been sent. We'll be in touch shortly.</span>
                </div>
              )}
              {status === "error" && (
                <div className="ct-form__feedback ct-form__feedback--error">
                  <FiAlertCircle size={18} />
                  <span>{errorMsg}</span>
                </div>
              )}

              <button
                type="submit"
                className="ct-form__submit"
                disabled={status === "sending" || status === "success"}
              >
                {status === "sending" ? (
                  <><FiLoader size={16} className="ct-spin" /> Sending…</>
                ) : status === "success" ? (
                  <><FiCheck size={16} /> Message Sent</>
                ) : (
                  <>Send Message <FiSend size={14} /></>
                )}
              </button>
            </form>
          </div>

          {/* ── Right: sidebar info ── */}
          <aside
            className={`ct-aside fade-in ${formInView ? "visible" : ""}`}
            style={{ transitionDelay: "0.15s" }}
          >

            {/* Workshop location */}
            <div className="ct-aside__block">
              <div className="ct-section-tag">OUR WORKSHOP</div>
              {loadingContact ? (
                <p className="ct-aside__loading">Loading contact details…</p>
              ) : (
                <ul className="ct-branches">
                  {addressLines.map((line, i) => (
                    <li key={i}>
                      {i === 0 && <span className="ct-branches__dot" />}
                      {i !== 0 && <span className="ct-branches__spacer" />}
                      {line}
                    </li>
                  ))}
                  {addressLink && addressLink !== "#" && (
                    <li>
                      <a
                        href={addressLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ct-aside__map-link"
                      >
                        <FiMapPin size={12} /> Open in Maps
                      </a>
                    </li>
                  )}
                </ul>
              )}
            </div>

            {/* Contact details */}
            {!loadingContact && (phoneLines.length > 0 || emailLines.length > 0) && (
              <div className="ct-aside__block">
                <div className="ct-section-tag">DIRECT CONTACT</div>
                <ul className="ct-certs">
                  {phoneLines.map((p, i) => (
                    <li key={`p-${i}`}>
                      <span><FiPhone size={12} style={{ marginRight: 6 }} />Phone {phoneLines.length > 1 ? i + 1 : ""}</span>
                      <strong><a href={`tel:${p.replace(/\s/g, "")}`} className="ct-certs__link">{p}</a></strong>
                    </li>
                  ))}
                  {emailLines.map((em, i) => (
                    <li key={`e-${i}`}>
                      <span><FiMail size={12} style={{ marginRight: 6 }} />Email {emailLines.length > 1 ? i + 1 : ""}</span>
                      <strong><a href={`mailto:${em}`} className="ct-certs__link">{em}</a></strong>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Quick call CTA */}
            {!loadingContact && phoneLines.length > 0 && (
              <div className="ct-aside__block ct-aside__block--cta">
                <p>Need urgent vehicle assistance?</p>
                {phoneLines.map((p, i) => (
                  <a
                    key={i}
                    href={`tel:${p.replace(/\s/g, "")}`}
                    className={`ct-aside__call-btn ${i > 0 ? "ct-aside__call-btn--secondary" : ""}`}
                  >
                    <FiPhone size={15} /> {p}
                  </a>
                ))}
              </div>
            )}

          </aside>
        </div>
      </section>

      {/* ══════ 4. MAP ══════ */}
      <section className="ct-map" ref={mapRef}>
        <div className={`ct-map__label-bar fade-in ${mapInView ? "visible" : ""}`}>
          <div className="container ct-map__label-inner">
            <div>
              <div className="ct-section-tag" style={{ marginBottom: 8 }}>WORKSHOP LOCATION</div>
              {loadingContact ? (
                <p className="ct-map__address">Loading address…</p>
              ) : (
                <p className="ct-map__address">{addressLines.join(", ")}</p>
              )}
            </div>
            {addressLink && addressLink !== "#" && (
              <a
                href={addressLink}
                target="_blank"
                rel="noopener noreferrer"
                className="ct-map__dir-btn"
              >
                Get Directions <FiArrowRight size={13} />
              </a>
            )}
          </div>
        </div>

        <div
          className={`ct-map__embed fade-in ${mapInView ? "visible" : ""}`}
          style={{ transitionDelay: "0.1s" }}
        >
          {iframeLink ? (
            <iframe
              title="Workshop Location"
              src={iframeLink}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          ) : (
            <div className="ct-map__placeholder">
              <FiMapPin size={40} />
              <p>Map not configured — add an iframe link in the admin panel.</p>
            </div>
          )}
        </div>
      </section>

    </main>
  );
}